/**
 * Builds the flat Certificate[] dashboard list from a fully-parsed FullSeafarerData.
 *
 * Intelligence applied (hardcoded from STCW 2010 Manila Amendments + DG Shipping India MMD circulars):
 *
 * 1. Documents (Passport, CDC) — if multiple entries exist for the same document type,
 *    only the one with the latest expiry is kept.
 *
 * 2. CoC (Certificate of Competency) — seafarers progress through grades over their career
 *    (Class IV → Class II → Class I for both deck and engine branches).
 *    Only the highest grade per branch is shown; lower grades have no operational value
 *    once superseded.
 *
 * 3. Course hierarchy — based on STCW chapter requirements and DG Shipping India practice:
 *    a. Superseding: if a higher-tier certificate is present, the lower-tier equivalent
 *       is dropped (e.g. AFF supersedes FPFF; MFA supersedes EFA; Advanced Tanker
 *       supersedes Basic Tanker; CMHB supersedes Crowd Management; GOC supersedes ROC;
 *       SSO supersedes Ship Security Awareness).
 *    b. Refresher/Revalidation collapsing: "BST Revalidation" and "BST" are the same
 *       certificate type — only the entry with the latest expiry is kept.
 *
 * Reference: STCW 2010 Manila Amendments, DG Shipping India MMD Circular 1 of 2020,
 *            MSC.1/Circ.1560 (STCW revalidation), MCA MIN 600 (ENG1 validity).
 */

import type { FullSeafarerData } from '@/lib/types/seafarer';
import type { Certificate, CertCategory, CertStatus } from '@/lib/db';
import { UNKNOWN_VERIFY_RULE, classifyLifecycle, resolveActionLabel } from '@/data/certificate-lifecycle-rules';
import { CERTIFICATE_CATALOG, normalizeCertificateName } from '@/data/certificate-catalog';
import { deduplicateCompetencyEntries, deduplicateEndorsementEntries, filterHistoricalRecords } from '@/lib/profile-merge';

// ── STCW validity lookup ──────────────────────────────────────────────────────

const VALIDITY_RULES: { test: RegExp; years: number; category: CertCategory }[] = [
  // ── 4 years ──────────────────────────────────────────────────────────────
  { test: /huet|helicopter underwater/i,             years: 4,  category: 'STCW' },

  // ── 2 years (medical fitness) ────────────────────────────────────────────
  { test: /medical fitness|eng1|fitness certificate|medical examination/i, years: 2, category: 'Medical' },

  // ── 10 years (travel / identity documents) ───────────────────────────────
  { test: /passport/i,                              years: 10, category: 'Flag State' },
  { test: /cdc|continuous discharge/i,              years: 10, category: 'Flag State' },

  // ── 5 years — STCW short courses ─────────────────────────────────────────
  { test: /advanc(?:e|ed)(?:\s+training\s+(?:for|in)\s+)?\s*fire\s*[- ]?\s*fighting|advanc(?:e|ed)\s+firefighting|fire\s*[- ]?\s*fighting.*advanc(?:e|ed)|\baff\b|\badff\b/i, years: 5, category: 'STCW' },
  { test: /arpa|radar simulator|radar observer/i,    years: 5, category: 'STCW' },
  { test: /basic safety training|bst\b/i,            years: 5, category: 'STCW' },
  { test: /bridge resource management|brm\b/i,       years: 5, category: 'STCW' },
  { test: /crowd management/i,                       years: 5, category: 'STCW' },
  { test: /crisis management|human behaviour/i,      years: 5, category: 'STCW' },
  { test: /dangerous goods|imdg|imsbc|imo dg/i,     years: 5, category: 'STCW' },
  { test: /ecdis/i,                                  years: 5, category: 'STCW' },
  { test: /electro.technical|eto course/i,           years: 5, category: 'STCW' },
  { test: /engine room resource|erm\b|errm\b/i,      years: 5, category: 'STCW' },
  { test: /fast rescue boat|frb\b/i,                 years: 5, category: 'STCW' },
  { test: /fire\s*prevention\s*(?:and|&)\s*fire\s*[- ]?\s*fighting|\bfp\s*&?\s*ff\b|\bfpff\b|basic\s+fire\s*[- ]?\s*fighting/i, years: 5, category: 'STCW' },
  { test: /gmdss|goc\b|roc\b|global maritime distress/i, years: 5, category: 'STCW' },
  { test: /high voltage|hv training/i,               years: 5, category: 'STCW' },
  { test: /isps|ship security awareness/i,           years: 5, category: 'STCW' },
  { test: /medical care officer|medical care/i,      years: 5, category: 'STCW' },
  { test: /medical first aid|mfa\b|mefa\b/i,         years: 5, category: 'STCW' },
  { test: /personal survival|pst\b/i,                years: 5, category: 'STCW' },
  { test: /proficiency in survival craft|rpscrb\b|pscrb\b/i, years: 5, category: 'STCW' },
  { test: /search and rescue|sar coordinator/i,      years: 5, category: 'STCW' },
  { test: /security awareness/i,                     years: 5, category: 'STCW' },
  { test: /ship security officer|sso\b/i,            years: 5, category: 'STCW' },
  { test: /tanker|atot|atct|atgt|basic tanker|oil tanker|chemical tanker|gas tanker/i, years: 5, category: 'STCW' },
  { test: /watchkeeping|officer of the watch|oow/i,  years: 5, category: 'STCW' },

  // ── 5 years — CoC / Endorsements ─────────────────────────────────────────
  { test: /certificate of competency|coc\b/i,        years: 5, category: 'Flag State' },
  { test: /endorsement/i,                            years: 5, category: 'Endorsement' },
];

export function getStcwValidity(name: string): { years: number; category: CertCategory; matched: boolean } {
  const catalogMatch = normalizeCertificateName(name);
  if (catalogMatch?.entry.cycleYears) {
    return {
      years: catalogMatch.entry.cycleYears,
      category: catalogMatch.entry.category,
      matched: true,
    };
  }

  for (const rule of VALIDITY_RULES) {
    if (rule.test.test(name)) return { years: rule.years, category: rule.category, matched: true };
  }
  return { years: 0, category: 'STCW', matched: false };
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function addYears(dateStr: string, years: number): string | null {
  if (!dateStr || dateStr === 'null' || dateStr === 'N/A') return null;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  d.setFullYear(d.getFullYear() + years);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function certStatus(expiryDate: string | null): { status: CertStatus; daysRemaining: number | null } {
  if (!expiryDate || expiryDate === 'null' || expiryDate === 'N/A') {
    return { status: 'valid', daysRemaining: null };
  }
  const parts = expiryDate.split('/');
  if (parts.length !== 3) return { status: 'valid', daysRemaining: null };
  const [dd, mm, yyyy] = parts;
  const expiry = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(expiry.getTime())) return { status: 'valid', daysRemaining: null };
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const diff = Math.ceil((expiry.getTime() - now.getTime()) / 86400000);
  if (diff < 0)   return { status: 'expired',       daysRemaining: diff };
  if (diff <= 7)  return { status: 'critical',      daysRemaining: diff };
  if (diff <= 90) return { status: 'expiring-soon', daysRemaining: diff };
  return { status: 'valid', daysRemaining: diff };
}

function expiryMs(expiryDate: string): number {
  const parts = expiryDate.split('/');
  if (parts.length !== 3) return 0;
  const [dd, mm, yyyy] = parts;
  return new Date(`${yyyy}-${mm}-${dd}T00:00:00`).getTime() || 0;
}

function dateMs(date: string): number {
  const parts = date.split('/');
  if (parts.length !== 3) return 0;
  const [dd, mm, yyyy] = parts;
  return new Date(`${yyyy}-${mm}-${dd}T00:00:00`).getTime() || 0;
}

function formatEndorsementName(e: {
  endorsementType?: string;
  tankerType?: string;
  level?: string;
}): string {
  const base = e.endorsementType?.trim() || 'Endorsement';
  const parts = [base];

  const tankerType = e.tankerType?.trim();
  if (tankerType && !base.toLowerCase().includes(tankerType.toLowerCase())) {
    parts.push(tankerType);
  } else if (/dc\s+endorsement/i.test(base)) {
    parts.push('Commodity not detected');
  }

  const level = e.level?.trim();
  if (level && !base.toLowerCase().includes(level.toLowerCase())) {
    parts.push(level);
  }

  return parts.join(' - ');
}

function recencyMs(cert: Certificate): number {
  return Math.max(expiryMs(cert.expiryDate), dateMs(cert.issueDate));
}

// ── Deduplication helpers ─────────────────────────────────────────────────────

/**
 * Exported so profile page can reuse the same grade-ranking logic.
 * CoC grade rank — higher number = more senior grade.
 * DG Shipping India progression (both deck and engine):
 *   Class IV (junior watchkeeper) → Class II (senior officer) → Class I (master/chief engineer)
 */
export function cocGradeRank(gradeName: string): number {
  const n = gradeName.toLowerCase();
  // Class I / Master / Chief Engineer = highest
  if (/class\s*i(?![iv\d])|class\s*1\b|master(?! at arms)|\bmaster\b|chief engineer/i.test(n)) return 4;
  // Class II / Chief Mate / Chief Officer / 2nd Engineer (unlimited)
  if (/class\s*ii(?!i)|class\s*2\b|chief mate|chief officer|second engineer.*unlimited/i.test(n)) return 3;
  // Class III
  if (/class\s*iii|class\s*3\b/i.test(n)) return 2;
  // Class IV / watchkeeper / OOW level
  if (/class\s*iv|class\s*4\b/i.test(n)) return 1;
  // Default: treat as mid-level
  return 2;
}

/** Is this cert name a CoC (Certificate of Competency)? */
function isCoC(cert: Certificate): boolean {
  return (
    cert.category === 'Flag State' &&
    /class\s*[i1-4v]|certificate of competency|\bcoc\b|chief officer|chief mate|chief engineer|second engineer|master(?! at arms)|officer of the watch|oow\b/i.test(cert.name)
  );
}

/** Is this cert an engine-branch CoC (vs deck)? */
function isCoCEngine(cert: Certificate): boolean {
  return /engine|engineer|meo\b|electro.technical/i.test(cert.name);
}

/**
 * Normalize a course name to a "base key" for refresher/revalidation deduplication.
 * Strips temporal qualifiers (Refresher, Revalidation, etc.) but preserves level
 * qualifiers (Basic, Advanced) since those represent different requirement tiers.
 */
function courseBaseKey(name: string): string {
  if (!name) return '__unknown__';
  const catalogMatch = normalizeCertificateName(name);
  if (catalogMatch) return catalogMatch.canonicalId;

  return name
    .toLowerCase()
    .replace(/\b(refresher|revalidation|renewal|update|re-?validation|re-?fresher|re-?assessment)\b/gi, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Course superseding rules — hardcoded from STCW 2010 Manila Amendments and
 * DG Shipping India practice.
 *
 * Rule: if ANY cert matching `superiorTest` is present in the list, ALL certs
 * matching any of `supersededTests` are removed.
 *
 * Why each rule exists:
 * - AFF > FPFF: STCW Reg. VI/3 (Advanced Fire Fighting) covers all VI/1-2 fire
 *   objectives. An officer with AFF does not separately list FPFF.
 * - MFA > EFA: STCW Reg. VI/4-1 (Medical First Aid) subsumes VI/1-3 (EFA).
 * - Advanced Tanker > Basic Tanker (per commodity): STCW Reg. V/1-1/V/1-2 —
 *   an advanced certificate implies the basic was completed.
 * - CMHB > Crowd Management: STCW Reg. V/2.1-2 (Crisis Management & Human
 *   Behaviour) satisfies the V/2.1-1 (Crowd Management) requirement for
 *   officers and key personnel.
 * - GOC > ROC: STCW Reg. IV/2 — GOC (unlimited) covers ROC (restricted) scope.
 * - SSO > Ship Security Awareness: STCW Reg. VI/5 — Ship Security Officer
 *   training subsumes VI/6-1 (awareness) and VI/6-2 (designated duties).
 * - Management-level ARPA > Operational-level ARPA: STCW Reg. II/2 /
 *   Table A-II/2 — management level covers operational.
 */
const SUPERSEDING_RULES: { superiorTest: RegExp; supersededTests: RegExp[] }[] = [
  // Advanced Fire Fighting (AFF) supersedes Fire Prevention & Fire Fighting (FPFF / Basic)
  {
    superiorTest: /advanc(?:e|ed)(?:\s+training\s+(?:for|in)\s+)?\s*fire\s*[- ]?\s*fighting|advanc(?:e|ed)\s+firefighting|fire\s*[- ]?\s*fighting.*advanc(?:e|ed)|\baff\b|\badff\b/i,
    supersededTests: [/fire\s*prevention\s*(?:and|&)\s*fire\s*[- ]?\s*fighting|\bfp\s*&?\s*ff\b|\bfpff\b|basic\s+fire\s*[- ]?\s*fighting/i],
  },

  // Advanced Oil Tanker Training supersedes Basic Oil Tanker Training
  {
    superiorTest: /advanced.*oil tanker|advanced tanker.*oil|\batot\b/i,
    supersededTests: [/basic.*oil tanker|basic tanker.*oil|\bbot\b.*oil|oil.*\bbot\b/i],
  },

  // Advanced Chemical Tanker Training supersedes Basic Chemical Tanker Training
  {
    superiorTest: /advanced.*chemical tanker|advanced tanker.*chemical|\batct\b/i,
    supersededTests: [/basic.*chemical tanker|basic tanker.*chemical|\bbot\b.*chem|chem.*\bbot\b/i],
  },

  // Advanced Gas / LPG / LNG Tanker supersedes Basic Gas Tanker
  {
    superiorTest: /advanced.*(?:gas|lpg|lng|liquefied).*tanker|advanced tanker.*(?:gas|lpg|lng)|\batgt\b/i,
    supersededTests: [/basic.*(?:gas|lpg|lng|liquefied).*tanker|basic tanker.*(?:gas|lpg|lng)/i],
  },

  // Crisis Management & Human Behaviour (CMHB) supersedes Crowd Management
  // (applies to officers and key personnel on passenger ships — Reg. V/2)
  {
    superiorTest: /crisis management.*human|cmhb\b|passenger ship.*crisis/i,
    supersededTests: [/crowd management(?!.*crisis)/i],
  },

  // GMDSS General Operator Certificate (GOC) supersedes Restricted Operator Certificate (ROC)
  {
    superiorTest: /\bgoc\b|general operator certificate/i,
    supersededTests: [/\broc\b|restricted operator certificate/i],
  },

  // Ship Security Officer (SSO) supersedes Ship Security Awareness and Designated Security Duties
  {
    superiorTest: /ship security officer|\bsso\b/i,
    supersededTests: [/ship security awareness|designated security duties/i],
  },

  // Management-level Radar/ARPA supersedes Operational-level
  {
    superiorTest: /radar.*management level|arpa.*management level|management level.*(?:radar|arpa)/i,
    supersededTests: [/radar.*operational level|arpa.*operational level|operational level.*(?:radar|arpa)/i],
  },

  // Leadership & Managerial Skills (management level) supersedes Leadership & Teamworking
  {
    superiorTest: /leadership.*managerial|managerial.*skill/i,
    supersededTests: [/leadership.*teamwork|teamwork.*leadership/i],
  },
];

const CATALOG_SUPERSEDING_RULES = CERTIFICATE_CATALOG.flatMap((entry) =>
  (entry.supersedes ?? []).map((supersededId) => ({
    superiorId: entry.canonicalId,
    supersededId,
  })),
);

function certCanonicalId(cert: Certificate): string | undefined {
  return cert.canonicalId ?? normalizeCertificateName(cert.name)?.canonicalId;
}

/** Count how many meaningful fields are populated — used to prefer the richer of two same-number records. */
function completenessScore(cert: Certificate): number {
  let score = 0;
  if (cert.name && cert.name !== 'Unknown' && cert.name !== 'Unknown Course') score += 2;
  if (cert.issueDate && cert.issueDate !== 'N/A') score += 2;
  if (cert.expiryDate && cert.expiryDate !== 'N/A') score += 2;
  if (cert.issuingAuthority && cert.issuingAuthority !== 'DG Shipping') score += 1;
  if (cert.stcwCode) score += 1;
  if (cert.canonicalId) score += 1;
  if (cert.originalName) score += 1;
  return score;
}

function suppressAlerts(cert: Certificate, by: Certificate, reason: string): Certificate {
  if (cert.id === by.id) return cert;
  return {
    ...cert,
    alertSuppressed: true,
    alertSuppressedBy: by.name,
    alertSuppressionReason: reason,
    actionLabel: 'Alert covered',
  };
}

function isQualifyingPscrbRefresher(cert: Certificate): boolean {
  if (certCanonicalId(cert) !== 'pscrb' || cert.status === 'expired' || cert.alertSuppressed) {
    return false;
  }
  const sourceName = `${cert.originalName ?? ''} ${cert.name}`;
  return /\brpscrb\b|refresher|combined.*(?:pst|personal survival)|including.*(?:pst|personal survival)/i.test(sourceName);
}

/**
 * Apply all intelligence rules to a raw certificate list without deleting
 * history. Covered/older certificates stay visible, but their expiry alerts are
 * suppressed so KPIs and notifications focus on the current requirement owner.
 */
function deduplicateCerts(certs: Certificate[]): Certificate[] {
  let result = [...certs];

  // ── Step 0: Same certificate/document number → keep the most complete record ──
  // Primary key: normalised certNumber (non-empty, non-N/A).
  // Tiebreak: completeness score first, then latest date (max of expiry/issue).
  const certNumberBuckets: Record<string, Certificate[]> = {};
  for (const cert of result) {
    const num = cert.certNumber;
    if (!num || num === 'N/A') continue;
    const key = num.trim().toLowerCase().replace(/[\s\-/]/g, '');
    (certNumberBuckets[key] ??= []).push(cert);
  }
  for (const group of Object.values(certNumberBuckets)) {
    if (group.length < 2) continue;
    const best = group.reduce((winner, cert) => {
      const ws = completenessScore(winner);
      const cs = completenessScore(cert);
      if (cs > ws) return cert;
      if (cs === ws && recencyMs(cert) > recencyMs(winner)) return cert;
      return winner;
    });
    result = result.map((cert) =>
      group.some((item) => item.id === cert.id) && cert.id !== best.id
        ? suppressAlerts(cert, best, `${best.name} (${best.certNumber}) is the more complete record; this duplicate is kept for history.`)
        : cert,
    );
  }

  // ── Step 1: Superseding rules ────────────────────────────────────────────
  for (const rule of CATALOG_SUPERSEDING_RULES) {
    const superior = result.find((c) => certCanonicalId(c) === rule.superiorId);
    if (!superior) continue;
    result = result.map((cert) =>
      certCanonicalId(cert) === rule.supersededId
        ? suppressAlerts(cert, superior, `${superior.name} covers this lower-tier requirement for alerts.`)
        : cert,
    );
  }

  for (const rule of SUPERSEDING_RULES) {
    const superior = result.find((c) => rule.superiorTest.test(c.originalName ?? c.name));
    if (!superior) continue;
    result = result.map((cert) =>
      rule.supersededTests.some((t) => t.test(cert.originalName ?? cert.name))
        ? suppressAlerts(cert, superior, `${superior.name} covers this lower-tier requirement for alerts.`)
        : cert,
    );
  }

  // MFA is the higher first-aid competency and satisfies EFA for alert
  // purposes. An explicitly expired MFA record cannot own the EFA alert.
  const currentMfa = result
    .filter((cert) =>
      certCanonicalId(cert) === 'medical_first_aid'
      && cert.status !== 'expired'
      && !cert.alertSuppressed,
    )
    .reduce<Certificate | undefined>(
      (best, cert) => (!best || recencyMs(cert) > recencyMs(best) ? cert : best),
      undefined,
    );
  if (currentMfa) {
    result = result.map((cert) =>
      certCanonicalId(cert) === 'efa'
        ? suppressAlerts(
          cert,
          currentMfa,
          `${currentMfa.name} satisfies the lower Elementary First Aid requirement.`,
        )
        : cert,
    );
  }

  // DGS Circular 19 of 2021: a qualifying RPSCRB/combined PSCRB refresher
  // includes RPST competencies. Original PSCRB certificates do not trigger
  // this coverage rule, and expired refreshers cannot own the PST alert.
  const qualifyingPscrb = result
    .filter(isQualifyingPscrbRefresher)
    .reduce<Certificate | undefined>(
      (best, cert) => (!best || recencyMs(cert) > recencyMs(best) ? cert : best),
      undefined,
    );
  if (qualifyingPscrb) {
    result = result.map((cert) =>
      certCanonicalId(cert) === 'pst'
        ? suppressAlerts(
          cert,
          qualifyingPscrb,
          `${qualifyingPscrb.name} includes the PST refresher competencies under DGS Circular 19 of 2021.`,
        )
        : cert,
    );
  }

  // ── Step 2: Document alert ownership (passport, CDC) ─────────────────────
  const docBuckets: Record<string, Certificate[]> = {};
  for (const cert of result) {
    const n = (cert.name ?? '').toLowerCase();
    if (cert.category === 'Flag State' && /passport/i.test(n)) {
      (docBuckets['passport'] ??= []).push(cert);
    } else if (cert.category === 'Flag State' && /\bcdc\b|continuous discharge/i.test(n)) {
      (docBuckets['cdc'] ??= []).push(cert);
    }
  }

  for (const group of Object.values(docBuckets)) {
    const best = group.reduce((current, cert) => (recencyMs(cert) > recencyMs(current) ? cert : current));
    result = result.map((cert) =>
      group.some((item) => item.id === cert.id) && cert.id !== best.id
        ? suppressAlerts(cert, best, `${best.name} is the latest document record; this older record is kept for history.`)
        : cert,
    );
  }

  // ── Step 3: CoC alert ownership — highest grade per branch ──────────────
  const cocBest: Record<string, Certificate> = {};
  const cocGroups: Record<string, Certificate[]> = {};

  for (const cert of result) {
    if (isCoC(cert)) {
      const branch = isCoCEngine(cert) ? 'engine' : 'deck';
      (cocGroups[branch] ??= []).push(cert);
      const existing = cocBest[branch];
      if (!existing) {
        cocBest[branch] = cert;
      } else {
        const myRank = cocGradeRank(cert.name);
        const exRank = cocGradeRank(existing.name);
        if (myRank > exRank || (myRank === exRank && expiryMs(cert.expiryDate) > expiryMs(existing.expiryDate))) {
          cocBest[branch] = cert;
        }
      }
    }
  }

  for (const [branch, group] of Object.entries(cocGroups)) {
    const best = cocBest[branch];
    result = result.map((cert) =>
      group.some((item) => item.id === cert.id) && cert.id !== best.id
        ? suppressAlerts(cert, best, `${best.name} is the highest/current CoC record for this branch; this older grade is kept for history.`)
        : cert,
    );
  }

  // ── Step 4: Course refresher/revalidation alert ownership ───────────────
  const courseBuckets: Record<string, Certificate[]> = {};

  for (const cert of result) {
    if (cert.category === 'Endorsement' && !cert.canonicalId) {
      continue;
    }

    if (['STCW', 'Endorsement', 'Proficiency', 'Medical'].includes(cert.category)) {
      const key = courseBaseKey(cert.name ?? '');
      (courseBuckets[key] ??= []).push(cert);
    }
  }

  for (const group of Object.values(courseBuckets)) {
    if (group.length < 2) continue;
    const best = group.reduce((current, cert) => (recencyMs(cert) > recencyMs(current) ? cert : current));
    result = result.map((cert) =>
      group.some((item) => item.id === cert.id) && cert.id !== best.id
        ? suppressAlerts(cert, best, `${best.name} is the latest record for this certificate family; this older record is kept for history.`)
        : cert,
    );
  }

  return result;
}

// ── Main builder ──────────────────────────────────────────────────────────────

function stableHash(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Generates a stable ID tied to the exact source Profile row. The immutable
 * sourceRecordId prevents two similar records from swapping certificate IDs
 * when their array order changes.
 *
 * Legacy/direct callers without a sourceRecordId use a deterministic fallback
 * built number-first, then type, authority, issue date, and STCW code.
 */
function makeCertId(
  canonicalId: string | undefined,
  name: string,
  certNumber: string,
  issueDate: string,
  stcwCode: string | undefined,
  issuingAuthority: string,
  sourceRecordId: string | undefined,
  idCounts: Map<string, number>,
): string {
  const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const sourceIdentity = sourceRecordId
    ?? [certNumber, canonicalId ?? name, issuingAuthority, issueDate, stcwCode ?? ''].join('|');
  const base = `dg:${slug(canonicalId ?? name)}:${stableHash(sourceIdentity)}`;
  const n = (idCounts.get(base) ?? 0) + 1;
  idCounts.set(base, n);
  if (n === 1) return base;
  // Only possible for truly identical legacy/direct inputs without row IDs.
  return `${base}:${n}`;
}

export function buildCertificatesFromSeafarerData(rawData: FullSeafarerData): Certificate[] {
  const data = filterHistoricalRecords(rawData);
  const raw: Certificate[] = [];
  const idCounts = new Map<string, number>();

  /**
   * Create one Certificate entry, applying lifecycle classification before committing.
   *
   * @param hasExplicitExpiry - True when the DG profile includes a parsed validUpto date.
   *   Passed to classifyLifecycle so that an explicit issuing-authority date is never
   *   overridden by our no-expiry/no-5yr-revalidation defaults.
   */
  const push = (
    name: string,
    certNumber: string,
    issuingAuthority: string,
    issueDate: string,
    expiryDate: string | null,
    category: CertCategory,
    hasExplicitExpiry = false,
    sourceRecordId?: string,
  ) => {
    const originalName = name || 'Unknown';
    const catalogMatch = normalizeCertificateName(originalName);
    const displayName = catalogMatch?.canonicalName ?? originalName;
    const rule = classifyLifecycle(originalName, hasExplicitExpiry)
      ?? (!hasExplicitExpiry && !expiryDate ? UNKNOWN_VERIFY_RULE : null);

    let finalStatus: CertStatus;
    let finalExpiry: string;
    let finalDays: number | null;
    let actionLabel: string | undefined;
    let requirementNote: string | undefined;
    let lifecycleType: string | undefined;
    let sourceRuleId: string | undefined;

    if (rule?.lifecycle === 'no_expiry') {
      // Permanent record — never show as expired.
      finalStatus = 'no-expiry';
      finalExpiry = 'N/A';
      finalDays = null;
      actionLabel = 'No expiry';
      requirementNote = rule.requirementNote;
      lifecycleType = rule.lifecycle;
      sourceRuleId = rule.id;
    } else if (rule?.lifecycle === 'no_five_year_revalidation') {
      // Don't auto-expire by age alone. If the DG profile contains an explicit
      // validUpto date, preserve that date while still showing a verify label.
      const computed = hasExplicitExpiry ? certStatus(expiryDate) : null;
      finalStatus = computed ? computed.status : 'verify';
      finalExpiry = computed ? expiryDate || 'N/A' : 'N/A';
      finalDays = computed ? computed.daysRemaining : null;
      actionLabel = resolveActionLabel(
        rule,
        computed ? (computed.status as 'valid' | 'expiring-soon' | 'critical' | 'expired') : 'valid',
      );
      requirementNote = rule.requirementNote;
      lifecycleType = rule.lifecycle;
      sourceRuleId = rule.id;
    } else if (rule?.lifecycle === 'unknown_verify') {
      finalStatus = 'verify';
      finalExpiry = 'N/A';
      finalDays = null;
      actionLabel = 'Verify requirement';
      requirementNote = rule.requirementNote;
      lifecycleType = rule.lifecycle;
      sourceRuleId = rule.id;
    } else {
      // Standard expiry-date logic — also used for refresher_required and revalidation_required
      // where the computed expiry IS the meaningful compliance date.
      const computed = certStatus(expiryDate);
      finalStatus = computed.status;
      finalExpiry = expiryDate || 'N/A';
      finalDays = computed.daysRemaining;
      if (rule && expiryDate) {
        // Translate the generic status into a more informative action label.
        actionLabel = resolveActionLabel(rule, computed.status as 'valid' | 'expiring-soon' | 'critical' | 'expired');
        requirementNote = rule.requirementNote;
        lifecycleType = rule.lifecycle;
        sourceRuleId = rule.id;
      } else if (rule) {
        finalStatus = 'verify';
        finalExpiry = 'N/A';
        finalDays = null;
        actionLabel = 'Verify requirement';
        requirementNote = rule.requirementNote;
        lifecycleType = rule.lifecycle;
        sourceRuleId = rule.id;
      }
    }

    raw.push({
      id: makeCertId(catalogMatch?.canonicalId, displayName, certNumber || 'N/A', issueDate || 'N/A', catalogMatch?.entry.stcwCode, issuingAuthority, sourceRecordId, idCounts),
      name: displayName,
      ...(catalogMatch && { originalName }),
      ...(catalogMatch && { canonicalId: catalogMatch.canonicalId }),
      ...(catalogMatch && { canonicalName: catalogMatch.canonicalName }),
      ...(catalogMatch?.shortCode && { shortCode: catalogMatch.shortCode }),
      ...(catalogMatch?.entry.stcwCode && { stcwCode: catalogMatch.entry.stcwCode }),
      certNumber: certNumber || 'N/A',
      issuingAuthority: issuingAuthority || 'DG Shipping',
      issueDate: issueDate || 'N/A',
      expiryDate: finalExpiry,
      status: finalStatus,
      daysRemaining: finalDays,
      category,
      ...(actionLabel    !== undefined && { actionLabel }),
      ...(requirementNote !== undefined && { requirementNote }),
      ...(lifecycleType  !== undefined && { lifecycleType }),
      ...(sourceRuleId   !== undefined && { sourceRuleId }),
    });
  };

  // 1. ALL modular STCW courses — compute expiry from validity rules if not in PDF
  for (const c of (data.modularCourses ?? [])) {
    const issueDate = c.dateOfIssue || c.attendedTo || 'N/A';
    const { years, category, matched } = getStcwValidity(c.courseName ?? '');
    const hasExplicitExpiry = !!(c.validUpto && c.validUpto !== 'null');
    const expiryDate = hasExplicitExpiry ? c.validUpto! : matched ? addYears(issueDate, years) : null;
    push(c.courseName ?? 'Unknown Course', c.certificateNo ?? 'N/A', c.trainingInstitute ?? 'DG Shipping', issueDate, expiryDate, category, hasExplicitExpiry, c._recordId);
  }

  // 2. Certificates of endorsement (DG CDC endorsements)
  for (const { endorsement: e } of deduplicateEndorsementEntries(data.certificatesOfEndorsement ?? [], data._provenanceMap)) {
    const hasExplicitExpiry = !!(e.validUpto && e.validUpto !== 'null');
    push(formatEndorsementName(e), e.certificateNo ?? 'N/A', e.issuedAt ?? 'MMD', e.dateOfIssue ?? 'N/A', e.validUpto ?? null, 'Endorsement', hasExplicitExpiry, e._recordId);
  }

  // 3. Certificates of competency (CoC) — all grades collected here; dedup step keeps best
  for (const { coc } of deduplicateCompetencyEntries(data.certificatesOfCompetency ?? [], data._provenanceMap)) {
    const issueDate = coc.issueDate || coc.certificateDelivered || 'N/A';
    const hasExplicitExpiry = !!(coc.validUpto && coc.validUpto !== 'null');
    const expiryDate = hasExplicitExpiry ? coc.validUpto! : addYears(issueDate, 5);
    push(coc.grade ?? 'CoC', coc.certificateNumber ?? 'N/A', `MMD${coc.issuingCountry ? ', ' + coc.issuingCountry : ''}`, issueDate, expiryDate, 'Flag State', hasExplicitExpiry, coc._recordId);
  }

  // 4. Key authorised documents — now includes INDoS (classified as no_expiry by lifecycle rules)
  for (const doc of (data.authorisedDocuments ?? [])) {
    const type = (doc.documentType ?? '').toLowerCase();
    // Include INDoS, EXN-45, Passport, CDC — lifecycle classifier handles expiry semantics.
    const isKey = ['passport', 'cdc', 'exn', 'indos'].some(k => type.includes(k));
    if (!isKey) continue;
    const hasExplicitExpiry = !!(doc.validUpto && doc.validUpto !== 'null');
    const { years, category, matched } = getStcwValidity(doc.documentType ?? '');
    const expiryDate = hasExplicitExpiry ? doc.validUpto! : matched ? addYears(doc.issuedDate ?? 'N/A', years) : null;
    push(doc.documentType ?? 'Document', doc.documentNumber ?? 'N/A', doc.issuedAt ?? 'Government of India', doc.issuedDate ?? 'N/A', expiryDate, category, hasExplicitExpiry, doc._recordId);
  }

  // Apply smart deduplication (superseding, document latest-wins, CoC highest-grade, refresher collapse)
  const deduped = deduplicateCerts(raw);

  // Sort: most urgent first; no-expiry certs go last (they need no action).
  const STATUS_SORT: Record<CertStatus, number> = {
    expired: 0, critical: 1, 'expiring-soon': 2, valid: 3, 'no-expiry': 4,
    verify: 5,
  };
  return deduped.sort((a, b) => {
    if (!!a.alertSuppressed !== !!b.alertSuppressed) return a.alertSuppressed ? 1 : -1;
    const diff = STATUS_SORT[a.status] - STATUS_SORT[b.status];
    if (diff !== 0) return diff;
    if (a.daysRemaining != null && b.daysRemaining != null) return a.daysRemaining - b.daysRemaining;
    return 0;
  });
}
